import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, MapPin } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", message: "Confirming payment…", order: null });
  const attemptsRef = useRef(0);
  const cartCleared = useRef(false);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id", order: null });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.payment_status === "paid") {
          if (!cartCleared.current) { clear(); cartCleared.current = true; }
          setState({ status: "paid", message: "Order received!", order: res.order });
          return;
        }
        if (res.status === "expired") {
          setState({ status: "expired", message: "Payment session expired.", order: null });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", message: "Payment check timed out. We'll email you when it's confirmed.", order: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "error", message: "Couldn't verify payment status.", order: null });
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-10">
      <div className="max-w-2xl w-full bg-[#1A1A1A] border-2 border-[#333] p-8 md:p-12">
        {state.status === "polling" && (
          <div className="text-center" data-testid="status-polling">
            <Loader2 className="w-12 h-12 animate-spin text-[#E60000] mx-auto mb-4" />
            <h1 className="font-heading text-4xl mb-2">Confirming…</h1>
            <p className="text-white/60">{state.message}</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="status-paid">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#E60000] flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold">Order Confirmed</div>
                <h1 className="font-heading text-4xl md:text-5xl">You're Eating Good</h1>
              </div>
            </div>
            {state.order && (
              <div className="border border-[#333] p-5 mb-6">
                <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  <Field label="Order ID" value={state.order.id?.slice(0, 8)} />
                  <Field label="Customer" value={state.order.customer_name} />
                  <Field label="Phone" value={state.order.customer_phone} />
                  <Field label="Fulfillment" value={state.order.fulfillment?.toUpperCase()} />
                  <Field label="Subtotal" value={`$${state.order.subtotal?.toFixed(2)}`} />
                  <Field label="Tax" value={`$${state.order.tax?.toFixed(2)}`} />
                  <Field label="Total" value={`$${state.order.total?.toFixed(2)}`} />
                  <Field label="Status" value={state.order.status} />
                </div>
                {state.order.lines?.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-[#333]">
                    <div className="text-xs uppercase tracking-widest text-white/60 mb-3">Items</div>
                    <ul className="space-y-2 text-sm">
                      {state.order.lines.map((l, i) => (
                        <li key={i} className="flex justify-between">
                          <span>{l.quantity}× {l.name}</span>
                          <span className="text-white/70">${l.line_total?.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="border-l-2 border-[#E60000] pl-4 text-sm text-white/70 mb-6 flex gap-2">
              <MapPin className="w-4 h-4 text-[#E60000] flex-shrink-0 mt-0.5" />
              {state.order?.fulfillment === "pickup"
                ? <span>Pickup at <strong className="text-white">3711 Lamar Ave, Memphis TN 38118</strong>. We'll text you when it's ready.</span>
                : <span>We'll deliver to <strong className="text-white">{state.order?.delivery_address}</strong> shortly.</span>}
            </div>
            <Link
              data-testid="back-home-btn"
              to="/"
              className="inline-block bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm px-6 h-12 leading-[3rem] border-2 border-transparent hover:border-white transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}
        {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
          <div className="text-center" data-testid={`status-${state.status}`}>
            <XCircle className="w-12 h-12 text-[#E60000] mx-auto mb-4" />
            <h1 className="font-heading text-4xl mb-2">Hmm…</h1>
            <p className="text-white/70 mb-6">{state.message}</p>
            <Link to="/" className="inline-block bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm px-6 h-12 leading-[3rem] border-2 border-transparent hover:border-white transition-colors">
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
    <div className="text-white font-semibold">{value || "—"}</div>
  </div>
);

export default CheckoutSuccess;
